/**
 * The pieces every auth screen is built from (M2.9, FR-PORTAL-01, rules §17, §48).
 *
 * Nothing here is clever. It exists so that five screens make the same promises: one
 * card, one heading, one error summary that takes focus, labelled controls with the
 * right `autocomplete`, and 44px targets at 390px. Getting those wrong once is a bug;
 * getting them wrong five times is a pattern, which is what this file prevents.
 *
 * Copy rule: short, plain sentences, and never a hint about whether an address has an
 * account (rules §21.4, SECURITY.md §4.2). "Check your email" is the answer whether or
 * not there was anyone to email.
 */

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { ApiError } from '@platform/api-client';
import { Alert, Card, CardBody, Input, cn, type InputProps } from '@platform/ui';

/* ------------------------------------------------------------------ *
 * The card
 * ------------------------------------------------------------------ */

export interface AuthCardProps {
  title: string;
  /** One sentence under the title. Optional - a screen that needs none has none. */
  lede?: ReactNode;
  /** Quiet links under the card: "Create an account", "Back to sign in". */
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * The funnel's card at the funnel's width. The heading is an `h1` because on these
 * screens the card *is* the page - there is no other title above it.
 */
export function AuthCard({ title, lede, footer, children }: AuthCardProps) {
  return (
    /*
     * `ha-auth` is what opts this subtree out of the funnel's blanket margin/padding
     * reset, so the design-system components keep their own spacing inside the funnel
     * shell. See the comment on that rule in `booking/booking.css`.
     */
    <div className="ha-auth">
      <Card>
        <CardBody>
          <h1 className="mb-1 text-xl font-bold text-ink">{title}</h1>
          {lede === undefined ? null : (
            <p className="mb-5 text-base leading-[1.5] text-neutral-500">{lede}</p>
          )}
          {children}
        </CardBody>
      </Card>
      {footer === undefined ? null : (
        <p className="mt-4 text-center text-base text-neutral-500">{footer}</p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Errors
 * ------------------------------------------------------------------ */

/**
 * The sentence to show for a failure.
 *
 * The API writes its own messages to be shown (API.md §1.6), so this mostly gets out of
 * the way. The one addition is the retry countdown: `429` carries `Retry-After`, and
 * "try again in 45 seconds" is far more useful than "too many attempts" on its own.
 * A locked account answers the same `429` on purpose, so this must never say "locked"
 * (SECURITY.md §4.4).
 */
export function describeError(error: unknown): string {
  // A screen that already knows what to say says it - `/auth/link` does this for a URL
  // that arrived without its token, which never reached the API at all.
  if (typeof error === 'string' && error.trim() !== '') return error;

  if (error instanceof ApiError) {
    if (error.code === 'RATE_LIMITED' && error.retryAfter !== undefined) {
      return `${error.message} You can try again in ${formatSeconds(error.retryAfter)}.`;
    }
    return error.message;
  }
  return 'Something went wrong. Please try again.';
}

function formatSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds} second${seconds === 1 ? '' : 's'}`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
}

export interface ErrorSummaryProps {
  /** An `ApiError`, a plain string, or null for "no error". */
  error: unknown;
}

/**
 * The error summary (rules §48): one region at the top of the form, announced when it
 * appears and given focus, so a screen-reader or keyboard user is told what happened
 * instead of having to hunt for it. `tabIndex={-1}` makes it focusable without putting
 * it in the tab order once read.
 */
export function ErrorSummary({ error }: ErrorSummaryProps) {
  const ref = useRef<HTMLDivElement>(null);
  const message = error === null || error === undefined ? null : describeError(error);

  useEffect(() => {
    if (message === null) return;
    ref.current?.focus();
  }, [message]);

  if (message === null) return null;

  return (
    <div ref={ref} tabIndex={-1} className="mb-4 outline-none">
      <Alert tone="error">{message}</Alert>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * The sign-in method switch
 * ------------------------------------------------------------------ */

export interface ModeTab<Value extends string> {
  value: Value;
  label: string;
}

export interface ModeTabsProps<Value extends string> {
  label: string;
  tabs: ModeTab<Value>[];
  value: Value;
  onChange: (value: Value) => void;
}

/**
 * The segmented control that picks a sign-in method.
 *
 * A real `tablist` with roving focus and arrow keys, not three buttons that look like
 * one: the three panels are alternative ways to do the same thing, which is exactly
 * what the tab pattern means, and a screen reader then announces "2 of 3" rather than
 * leaving the patient to infer the relationship.
 */
export function ModeTabs<Value extends string>({
  label,
  tabs,
  value,
  onChange,
}: ModeTabsProps<Value>) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  const move = (delta: number) => {
    const index = tabs.findIndex((tab) => tab.value === value);
    const next = tabs[(index + delta + tabs.length) % tabs.length];
    if (next === undefined) return;
    onChange(next.value);
    refs.current[next.value]?.focus();
  };

  const jumpTo = (index: number) => {
    const next = tabs[index];
    if (next === undefined) return;
    onChange(next.value);
    refs.current[next.value]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      move(1);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      move(-1);
    } else if (event.key === 'Home') {
      /* M13.7: the ARIA tab pattern's first and last, which `admin/components/tabs.tsx`
         already had and this strip did not. */
      event.preventDefault();
      jumpTo(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      jumpTo(tabs.length - 1);
    }
  };

  return (
    <div
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      className="mb-5 grid grid-cols-3 gap-1 rounded-button border border-neutral-200 bg-neutral-50 p-1"
    >
      {tabs.map((tab) => {
        const selected = tab.value === value;
        return (
          <button
            key={tab.value}
            ref={(node) => {
              refs.current[tab.value] = node;
            }}
            type="button"
            role="tab"
            id={`auth-tab-${tab.value}`}
            aria-selected={selected}
            aria-controls={`auth-panel-${tab.value}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => {
              onChange(tab.value);
            }}
            className={cn(
              'min-h-11 cursor-pointer rounded-[9px] px-2 text-sm font-semibold',
              'transition-[background-color,color] duration-200',
              selected
                ? 'bg-surface text-primary-accessible shadow-card'
                : 'bg-transparent text-neutral-600 hover:text-ink',
            )}
          >
            {/*
             * The selected tab is not signalled by colour alone: it is the one with the
             * white surface, and `aria-selected` carries the state for assistive tech
             * (rules §48.5).
             */}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export interface TabPanelProps<Value extends string> {
  value: Value;
  children: ReactNode;
}

export function TabPanel<Value extends string>({ value, children }: TabPanelProps<Value>) {
  return (
    <div role="tabpanel" id={`auth-panel-${value}`} aria-labelledby={`auth-tab-${value}`}>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Controls
 * ------------------------------------------------------------------ */

export type PasswordInputProps = Omit<InputProps, 'type'> & {
  /** "current-password" on sign-in, "new-password" on register and reset. */
  autoComplete: 'current-password' | 'new-password';
};

/**
 * A password field with a show/hide toggle.
 *
 * The toggle is a real button with an accessible name that changes with the state, and
 * it is inside the field's box rather than beside it so the 44px target does not push
 * the input narrow on a 390px screen.
 */
export function PasswordInput({ className, ...rest }: PasswordInputProps) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="relative">
      <Input {...rest} type={visible ? 'text' : 'password'} className={cn('pr-12', className)} />
      <button
        type="button"
        onClick={() => {
          setVisible((current) => !current);
        }}
        aria-pressed={visible}
        aria-label={visible ? 'Hide password' : 'Show password'}
        className="absolute top-1/2 right-1 flex size-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-input text-neutral-500 hover:text-ink"
      >
        <EyeIcon crossed={visible} />
      </button>
    </div>
  );
}

/**
 * The eye, inline.
 *
 * `lucide-react` is a dependency of `@platform/ui`, not of this app, and the funnel's
 * own icons are inline SVG at `strokeWidth 2` (tokens doc §8.2). One 18px glyph is not
 * worth a second copy of an icon set in the patient bundle.
 */
function EyeIcon({ crossed }: { crossed: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
      {crossed ? <path d="m3 3 18 18" /> : null}
    </svg>
  );
}

export type OtpInputProps = Omit<InputProps, 'type' | 'inputMode' | 'autoComplete'>;

/**
 * The six-digit code field.
 *
 * One input, not six boxes: six boxes break paste, break the browser's own SMS/email
 * code autofill, and are a well-known screen-reader hazard. `autocomplete="one-time-code"`
 * is what lets iOS and Android offer the code above the keyboard, and `inputmode`
 * brings up the number pad without making the field reject a pasted code.
 */
export function OtpInput({ className, ...rest }: OtpInputProps) {
  return (
    <Input
      {...rest}
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={6}
      className={cn('text-center text-2xl tracking-[8px] tabular-nums', className)}
    />
  );
}

/* ------------------------------------------------------------------ *
 * Resend cooldown
 * ------------------------------------------------------------------ */

export interface Cooldown {
  /** Seconds left, 0 when the action is available. */
  remaining: number;
  start: () => void;
}

/**
 * The resend cooldown.
 *
 * A countdown in state rather than a timestamp comparison: the only thing the screen
 * needs is "how many seconds are left", the interval already ticks once a second to
 * repaint it, and this way there is no clock to inject and no drift to reason about.
 * It is a courtesy, not a control - the real limit is the API's (5 per 15 minutes per
 * address, API.md §1.9), which answers `429` whatever the button does.
 */
export function useCooldown(seconds: number): Cooldown {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (remaining <= 0) return;
    const timer = setInterval(() => {
      setRemaining((current) => (current <= 1 ? 0 : current - 1));
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [remaining]);

  const start = useCallback(() => {
    setRemaining(seconds);
  }, [seconds]);

  return { remaining, start };
}

/* ------------------------------------------------------------------ *
 * Small helpers
 * ------------------------------------------------------------------ */

/** A stable id for a field that several components have to reference. */
export function useFieldId(prefix: string): string {
  const generated = useId();
  return `${prefix}${generated}`;
}

/** A link that looks like the funnel's: primary, no underline until hover. */
export function QuietLink({
  href,
  onClick,
  children,
}: {
  href: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      onClick={onClick}
      className="inline-flex min-h-11 items-center text-primary-accessible underline underline-offset-2 hover:text-primary-accessible-hover"
    >
      {children}
    </a>
  );
}
